import React, { useEffect, useState } from 'react'
import { BiChevronDown } from 'react-icons/bi'
import { FiFilter, FiX } from 'react-icons/fi'

const readOpenState = (storageKey) => {
    try {
        return localStorage.getItem(storageKey) === '1';
    } catch {
        return false;
    }
};

export const filterChipStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    maxWidth: 260,
    paddingBlock: 4,
    paddingInlineStart: 10,
    paddingInlineEnd: 6,
    borderRadius: 999,
    background: '#eef2ff',
    border: '1px solid #e0e7ff',
    color: '#3730a3',
    fontSize: 11.5,
    fontWeight: 700,
    animation: 'cf-chip-in .22s ease-out',
};

export const filterChipRemoveStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: 'none',
    background: '#c7d2fe',
    color: '#3730a3',
    cursor: 'pointer',
    flexShrink: 0,
};

// صندوق فلاتر بيتلم: زرار "الفلاتر" بعدد الفلاتر الشغالة، واللوحة بتتفتح وتتقفل بأنيميشن وبتفضّي مكانها.
// لما تكون مقفولة بيفضل ظاهر ملخص الفلاتر كـ chips تقدر تشيلها. مستخدم في التذاكر والتقارير والمشاريع.
//  - chips: [{ key, label, value, onRemove }]
//  - leading: (openPanel) => عناصر قبل الـ chips (زي شريحة الفترة الزمنية)
//  - footer: اللي يفضل ظاهر تحت اللوحة دايماً (الحالات / البحث)
const CollapsibleFilters = ({
    lang = 'ar',
    storageKey,
    chips = [],
    activeCount,
    leading,
    onClearAll,
    footer,
    children,
}) => {
    const isAr = lang === 'ar';
    const [open, setOpen] = useState(() => readOpenState(storageKey));
    // أثناء الأنيميشن المحتوى بيتقص، وبعدها بيتساب عشان القوائم المنسدلة تظهر كاملة.
    const [settled, setSettled] = useState(true);
    const count = activeCount ?? chips.length;

    useEffect(() => {
        try {
            localStorage.setItem(storageKey, open ? '1' : '0');
        } catch {
            // التخزين مش متاح — مش مهم.
        }
    }, [open, storageKey]);

    const toggle = () => {
        setSettled(false);
        setOpen((value) => !value);
    };
    const openPanel = () => {
        if (!open) toggle();
    };

    return (
        <div style={{
            background: '#fff',
            borderRadius: 16,
            border: '1px solid #e2e8f0',
            padding: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
        }}>
            <style>{`
                @keyframes cf-chip-in { from { opacity: 0; transform: scale(.85); } to { opacity: 1; transform: scale(1); } }
                .cf-toggle:hover { border-color: #a5b4fc !important; background: #f5f7ff !important; }
            `}</style>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <button
                    type="button"
                    className="cf-toggle"
                    onClick={toggle}
                    aria-expanded={open}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        height: 34,
                        padding: '0 12px',
                        borderRadius: 10,
                        border: `1px solid ${open ? '#a5b4fc' : '#e2e8f0'}`,
                        background: open ? '#eef2ff' : '#fff',
                        color: '#1e1b4b',
                        fontSize: 12.5,
                        fontWeight: 800,
                        cursor: 'pointer',
                        transition: 'all .2s',
                    }}
                >
                    <FiFilter size={14} />
                    {isAr ? 'الفلاتر' : 'Filters'}
                    {count > 0 && (
                        <span style={{ minWidth: 20, height: 20, padding: '0 6px', borderRadius: 999, background: '#4f46e5', color: '#fff', fontSize: 11, fontWeight: 900, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            {count}
                        </span>
                    )}
                    <BiChevronDown size={18} style={{ transition: 'transform .3s', transform: open ? 'rotate(180deg)' : 'none', color: '#64748b' }} />
                </button>

                {leading?.(openPanel)}

                {chips.map((item) => (
                    <span key={item.key} style={filterChipStyle} title={`${item.label}: ${item.value}`}>
                        <span style={{ color: '#6366f1', fontWeight: 600, whiteSpace: 'nowrap' }}>{item.label}:</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.value}</span>
                        <button type="button" aria-label={isAr ? 'إزالة الفلتر' : 'Remove filter'} onClick={item.onRemove} style={filterChipRemoveStyle}>
                            <FiX size={11} />
                        </button>
                    </span>
                ))}

                {count > 1 && onClearAll && (
                    <button
                        type="button"
                        onClick={onClearAll}
                        style={{ border: 'none', background: 'none', color: '#dc2626', fontSize: 12, fontWeight: 800, cursor: 'pointer', padding: '4px 6px' }}
                    >
                        {isAr ? 'مسح الكل' : 'Clear all'}
                    </button>
                )}
            </div>

            {/* اللوحة: grid rows 0fr ↔ 1fr عشان الارتفاع يتحرك بنعومة */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateRows: open ? '1fr' : '0fr',
                    opacity: open ? 1 : 0,
                    transform: open ? 'translateY(0)' : 'translateY(-6px)',
                    transition: 'grid-template-rows .35s cubic-bezier(.4,0,.2,1), opacity .25s ease, transform .3s ease',
                    marginTop: open ? 0 : -12,
                    // القوائم المنسدلة جوه اللوحة لازم تظهر فوق اللي تحتها (الحالات / البحث).
                    position: 'relative',
                    zIndex: open ? 30 : 'auto',
                }}
                onTransitionEnd={(e) => { if (e.propertyName === 'grid-template-rows') setSettled(true); }}
                aria-hidden={!open}
            >
                <div style={{ minHeight: 0, overflow: open && settled ? 'visible' : 'hidden' }}>
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-end',
                        gap: 12,
                        padding: 12,
                        borderRadius: 12,
                        background: '#f8fafc',
                        border: '1px dashed #e2e8f0',
                        visibility: open || !settled ? 'visible' : 'hidden',
                    }}>
                        {children}
                    </div>
                </div>
            </div>

            {footer && <div style={{ position: 'relative', zIndex: 1 }}>{footer}</div>}
        </div>
    );
};

export default CollapsibleFilters
